import React, { useMemo, useState } from 'react';
import { Stack, useRouter, useLocalSearchParams } from 'expo-router';
import { Platform, Pressable, View, TouchableOpacity, useWindowDimensions } from 'react-native';
import { Menu, MenuItem, MenuItemLabel } from '@/components/gluestack/menu';
import { Delete02Icon, ArrowLeft01Icon } from '@hugeicons-pro/core-stroke-rounded';
import { Cancel01Icon, City03Icon } from '@hugeicons-pro/core-solid-rounded';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { MenuItemIcon } from '@/components/ui/MenuItemIcon';
import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
} from '@/components/gluestack/alert-dialog';
import { Button, ButtonText } from '@/components/gluestack/button';
import { ScrollView } from '@/components/gluestack/scroll-view';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Heading } from '@/components/gluestack/heading';
import { Text } from '@/components/gluestack/text';
import { Spinner } from '@/components/gluestack/spinner';
import { useTranslation } from 'react-i18next';

import { CommunityThemeScope } from '@/components/ui/CommunityThemeScope';
import { communities, communityCategories, CommunityCode } from '@/constants/communities';
import { FEED_ROUTES } from '@/constants/routes';
import { useCommunitySwitch } from '@/hooks/useCommunitySwitch';
import { useDefaultFeed } from '@/hooks/useDefaultFeed';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { Image, type ImageSource } from 'expo-image';
import Animated, { useSharedValue, useAnimatedStyle, withTiming } from 'react-native-reanimated';
import { useAuth } from '@/components/providers/AuthProvider';
import { WebDocumentTitle } from '@/components/layout/WebDocumentTitle';

type CommunitySiteCode = CommunityCode;

const CommunityTile = React.memo(function CommunityTile({ code }: { code: CommunitySiteCode }) {
  const router = useRouter();
  const scale = useSharedValue(1);
  const isLargeScreen = useIsLargeScreen();
  const isPressed = useSharedValue(false);
  const isHovered = useSharedValue(false);

  const animatedStyle = useAnimatedStyle(() => ({ transform: [{ scale: scale.value }] }));

  const handlePressIn = () => {
    isPressed.value = true;
    scale.value = withTiming(0.98, { duration: 100 });
  };
  const handlePressOut = () => {
    isPressed.value = false;
    scale.value = withTiming(Platform.OS === 'web' && isHovered.value ? 1.02 : 1, {
      duration: 120,
    });
  };

  const handleHoverIn = () => {
    if (Platform.OS !== 'web') return;
    isHovered.value = true;
    if (!isPressed.value) {
      scale.value = withTiming(1.04, { duration: 120 });
    }
  };
  const handleHoverOut = () => {
    if (Platform.OS !== 'web') return;
    isHovered.value = false;
    if (!isPressed.value) {
      scale.value = withTiming(1, { duration: 120 });
    }
  };

  return (
    <Pressable
      onPress={() => {
        router.push({ pathname: '/communities/[community]', params: { community: code } });
      }}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      className="w-full">
      <Animated.View style={[{ width: '100%' }, animatedStyle]}>
        <View className="aspect-[660/420] w-full overflow-hidden rounded-lg">
          <Image
            source={communities[code].tileBanner as ImageSource}
            className="h-full w-full"
            contentFit="cover"
          />
        </View>
      </Animated.View>
      {isLargeScreen && (
        <VStack className="mt-2">
          <Text className="font-medium">{communities[code].name}</Text>
        </VStack>
      )}
    </Pressable>
  );
});

export default function CommunitiesOverview() {
  const { t } = useTranslation(['default', 'domain']);
  const isLarge = useIsLargeScreen();
  const { getCredentials, forget, lastUsedCommunity } = useAuth();
  const { performCommunitySwitch } = useCommunitySwitch();
  // Grokio-meta surface: the directory brands itself NEUTRAL locally (scoped
  // vars). The global theme is never touched while browsing — it stays owned
  // by ThemeProvider — so gesture-back exits can't leak a theme. The portaled
  // confirm dialog escapes the scope but only uses neutral/negative buttons,
  // which no theme redefines. Community-specific screens (detail preview,
  // sign-in, sign-up) scope their own branding the same way.
  const { defaultFeed } = useDefaultFeed();
  const { from } = useLocalSearchParams();
  const { width: screenWidth } = useWindowDimensions();
  const tileWidth = Math.round(screenWidth * 0.4);
  const [loadingCommunity, setLoadingCommunity] = useState<CommunitySiteCode | null>(null);
  const [confirmRemoveCode, setConfirmRemoveCode] = useState<CommunitySiteCode | null>(null);

  const myCommunities: CommunitySiteCode[] = useMemo(() => {
    const all = Object.keys(communities) as CommunitySiteCode[];
    return all.filter((code) => !!getCredentials(code));
  }, [getCredentials]);

  const handleRemove = async () => {
    if (!confirmRemoveCode) return;
    try {
      await forget(confirmRemoveCode);
      setConfirmRemoveCode(null);
    } catch (error) {
      console.error('Failed to remove community:', error);
      setConfirmRemoveCode(null);
    }
  };

  const handleCommunitySwitch = async (code: CommunitySiteCode) => {
    setLoadingCommunity(code);
    try {
      // Theme flip, auth switch and the navigation reset run behind the
      // full-screen overlay (see useCommunitySwitch). Land directly on the
      // default feed like the header switcher does — a '/' landing re-enters
      // through the index gate and remounts the app tree a second time,
      // which peeked through as a white frame before the overlay dropped.
      await performCommunitySwitch(code, FEED_ROUTES[defaultFeed]);
    } catch (error) {
      // Handle error if needed
      console.error('Failed to switch community:', error);
    } finally {
      setLoadingCommunity(null);
    }
  };

  return (
    <>
      <WebDocumentTitle title="Communities" />
      <Stack.Screen
        options={{
          title: '',
          headerLeft:
            Platform.OS !== 'web' && from === 'profileMenu' && lastUsedCommunity
              ? () => (
                  <TouchableOpacity
                    accessibilityLabel={t('common.back')}
                    onPress={() => handleCommunitySwitch(lastUsedCommunity as CommunitySiteCode)}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    activeOpacity={0.7}>
                    <HugeIconsWrapper icon={ArrowLeft01Icon} size={28} className="color-white" />
                  </TouchableOpacity>
                )
              : undefined,
        }}
      />
      <CommunityThemeScope community="default">
        <ScrollView className="bg-background-0" contentContainerClassName="pb-8">
          <VStack className="mx-auto w-full max-w-7xl gap-4 p-4 md:pt-6">
            {Platform.OS !== 'web' && myCommunities.length > 0 && (
              <VStack className="relative gap-3 overflow-visible border-b-2 border-neutral-200 pb-6 dark:border-neutral-800">
                <View pointerEvents="none" className="absolute right-0 top-0">
                  <HugeIconsWrapper
                    icon={City03Icon}
                    size={104}
                    className="color-neutral-300 dark:color-background-100"
                  />
                </View>
                <Heading size="xl">{t('communities.myCommunities')}</Heading>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <HStack className="gap-4 pr-4">
                    {myCommunities.map((code) => {
                      const isLoading = loadingCommunity === code;
                      return (
                        <Menu
                          key={code}
                          placement="bottom left"
                          offset={8}
                          trigger={(props: any) => (
                            <TouchableOpacity
                              {...props}
                              activeOpacity={0.8}
                              onPress={() => handleCommunitySwitch(code)}
                              onLongPress={props.onPress}
                              disabled={isLoading}>
                              <View className="relative aspect-square size-16 overflow-hidden rounded-full">
                                <Image
                                  source={communities[code].icon as ImageSource}
                                  className={`h-full w-full ${isLoading ? 'opacity-50' : ''}`}
                                  contentFit="cover"
                                />
                                {isLoading && (
                                  <View className="absolute inset-0 items-center justify-center rounded-full bg-neutral-800/80">
                                    <Spinner size="small" color="white" />
                                  </View>
                                )}
                              </View>
                            </TouchableOpacity>
                          )}>
                          <MenuItem
                            key="remove"
                            textValue={t('communities.menu.remove')}
                            onPress={() => setConfirmRemoveCode(code)}>
                            <MenuItemIcon icon={Delete02Icon} className="color-error-500" />
                            <MenuItemLabel>{t('communities.menu.remove')}</MenuItemLabel>
                          </MenuItem>
                        </Menu>
                      );
                    })}
                  </HStack>
                </ScrollView>
              </VStack>
            )}

            {communityCategories.map((category) => (
              <VStack key={category.key} className={`gap-4`}>
                <Heading size="xl">
                  {t(`CommunityCategories.${category.key}.Name`, { ns: 'domain' })}
                </Heading>
                {isLarge ? (
                  <HStack className="gap-8">
                    {category.communities.map((code) => {
                      const caption = t(`CommunityInfo.communities.${code}.caption`, {
                        ns: 'domain',
                      }) as string;
                      return (
                        <VStack key={code} className="w-80">
                          <CommunityTile code={code as CommunitySiteCode} />
                          <Text size="sm" className="text-typography-600">
                            {caption}
                          </Text>
                        </VStack>
                      );
                    })}
                  </HStack>
                ) : (
                  <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                    <HStack className="gap-4 pr-4">
                      {category.communities.map((code) => {
                        const caption = t(`CommunityInfo.communities.${code}.caption`, {
                          ns: 'domain',
                        }) as string;
                        return (
                          <VStack key={code} className="gap-1" style={{ width: tileWidth }}>
                            <CommunityTile code={code as CommunitySiteCode} />
                            <Text size="sm" className="text-typography-600">
                              {caption}
                            </Text>
                          </VStack>
                        );
                      })}
                    </HStack>
                  </ScrollView>
                )}
              </VStack>
            ))}
          </VStack>
        </ScrollView>
      </CommunityThemeScope>
      <AlertDialog
        isOpen={!!confirmRemoveCode}
        onClose={() => setConfirmRemoveCode(null)}
        size="md">
        <AlertDialogBackdrop />
        <AlertDialogContent>
          <AlertDialogHeader>
            <Heading size="md">{t('communities.delete.confirmTitle')}</Heading>
            <AlertDialogCloseButton>
              <HugeIconsWrapper icon={Cancel01Icon} size={20} className="color-neutral-500" />
            </AlertDialogCloseButton>
          </AlertDialogHeader>
          <AlertDialogBody className="mt-3">
            <Text>
              {t('communities.delete.confirmMessage', {
                communityName: confirmRemoveCode ? communities[confirmRemoveCode].name : '',
              })}
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter className="mt-6 gap-3">
            <Button variant="outline" action="neutral" onPress={() => setConfirmRemoveCode(null)}>
              <ButtonText>{t('common.cancel')}</ButtonText>
            </Button>
            <Button action="negative" onPress={handleRemove}>
              <ButtonText>{t('common.delete')}</ButtonText>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
